import { useEffect, useRef, useState } from "react";
import { Play, Pause, Square, Volume2, ChevronDown } from "lucide-react";

const PREF_KEY = "cpd_voice_prefs_v1";

function loadPrefs() {
  try {
    return JSON.parse(localStorage.getItem(PREF_KEY) || "{}");
  } catch {
    return {};
  }
}
function savePrefs(p) {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

/**
 * Browser-native TTS controls.
 * Props:
 *  - getText: async () => string  (loaded only when user presses Play to avoid pre-fetching PDFs)
 *  - defaultRate, defaultPitch: from branding
 *  - accent: hex color for buttons
 */
export default function VoiceOver({ getText, defaultRate = 1, defaultPitch = 1, accent = "#002e7a" }) {
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [voices, setVoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const utterRef = useRef(null);

  const initial = loadPrefs();
  const [voiceURI, setVoiceURI] = useState(initial.voiceURI || "");
  const [rate, setRate] = useState(initial.rate ?? defaultRate);

  useEffect(() => {
    if (!supported) return;
    const load = () => {
      const list = window.speechSynthesis.getVoices() || [];
      setVoices(list);
      if (!voiceURI && list.length) {
        // Try to pick a good default: English voice
        const en = list.find((v) => /en[-_]?GB/i.test(v.lang)) ||
                   list.find((v) => /^en/i.test(v.lang)) || list[0];
        if (en) setVoiceURI(en.voiceURI);
      }
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported]);

  useEffect(() => {
    savePrefs({ voiceURI, rate });
  }, [voiceURI, rate]);

  const stopAll = () => {
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setState("idle");
  };

  const play = async () => {
    if (!supported) return;
    if (state === "paused") {
      window.speechSynthesis.resume();
      setState("playing");
      return;
    }
    if (state === "playing") {
      window.speechSynthesis.pause();
      setState("paused");
      return;
    }
    setState("loading");
    let text = "";
    try {
      text = (await getText()) || "";
    } catch {
      setState("idle");
      return;
    }
    text = text.trim();
    if (!text) {
      setState("idle");
      return;
    }
    // Split long text into chunks to avoid browser limits (~32KB)
    const chunks = [];
    const MAX = 200; // sentences-ish
    const sentences = text.replace(/\s+/g, " ").split(/(?<=[.!?])\s+/);
    let current = "";
    for (const s of sentences) {
      if ((current + " " + s).length > MAX) {
        if (current) chunks.push(current);
        current = s;
      } else {
        current = current ? current + " " + s : s;
      }
    }
    if (current) chunks.push(current);

    const selectedVoice = voices.find((v) => v.voiceURI === voiceURI);
    stopAll();
    setState("playing");
    let i = 0;
    const speakNext = () => {
      if (i >= chunks.length) {
        setState("idle");
        return;
      }
      const u = new SpeechSynthesisUtterance(chunks[i++]);
      if (selectedVoice) u.voice = selectedVoice;
      u.rate = Number(rate) || 1;
      u.pitch = Number(defaultPitch) || 1;
      u.onend = speakNext;
      u.onerror = () => setState("idle");
      utterRef.current = u;
      window.speechSynthesis.speak(u);
    };
    speakNext();
  };

  if (!supported) {
    return (
      <div data-testid="voiceover-unsupported" className="text-xs font-mono text-muted-foreground">
        Voice-over not supported in this browser.
      </div>
    );
  }

  return (
    <div data-testid="voiceover" className="inline-flex items-stretch gap-0 border border-border rounded-sm overflow-hidden">
      <button
        type="button"
        onClick={play}
        data-testid="voiceover-play"
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium hover:opacity-90 transition-opacity"
        style={{ backgroundColor: accent, color: "#fff" }}
      >
        {state === "playing" ? (
          <><Pause size={14} strokeWidth={1.5} /> Pause</>
        ) : state === "paused" ? (
          <><Play size={14} strokeWidth={1.5} /> Resume</>
        ) : state === "loading" ? (
          <><Volume2 size={14} strokeWidth={1.5} className="animate-pulse" /> Loading…</>
        ) : (
          <><Play size={14} strokeWidth={1.5} /> Listen</>
        )}
      </button>
      {(state === "playing" || state === "paused") && (
        <button
          type="button"
          onClick={stopAll}
          data-testid="voiceover-stop"
          className="px-3 py-2 text-sm hover:bg-secondary transition-colors"
          title="Stop"
        >
          <Square size={14} strokeWidth={1.5} />
        </button>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="voiceover-settings"
        className="px-3 py-2 text-sm hover:bg-secondary transition-colors inline-flex items-center gap-1"
        title="Voice settings"
      >
        <ChevronDown size={12} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute mt-12 z-30 bg-background border border-border shadow-lg p-4 w-72 right-6 md:right-12">
          <div className="eyebrow mb-2">/ Voice</div>
          <select
            data-testid="voiceover-voice-select"
            value={voiceURI}
            onChange={(e) => setVoiceURI(e.target.value)}
            className="w-full border border-border rounded-sm h-9 px-2 text-sm bg-background"
          >
            {voices.map((v) => (
              <option key={v.voiceURI} value={v.voiceURI}>
                {v.name} — {v.lang}
              </option>
            ))}
          </select>
          <div className="eyebrow mt-4 mb-2">/ Speed — {Number(rate).toFixed(2)}x</div>
          <input
            type="range"
            min="0.5"
            max="1.75"
            step="0.05"
            value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            data-testid="voiceover-rate"
            className="w-full"
          />
          <div className="mt-2 flex justify-between text-[10px] font-mono text-muted-foreground">
            <span>0.5x</span><span>1x</span><span>1.75x</span>
          </div>
        </div>
      )}
    </div>
  );
}
